/**
 * PaymentRecoveriesPage — Admin overview of failed Stripe payment recovery attempts.
 *
 * Lists all active + resolved retry attempts, shows stats, and lets admin
 * manually resend the recovery email.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Send, Mail, Copy } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_STYLES = {
  pending: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', label: 'Wachtend' },
  sent: { bg: 'rgba(59,130,246,0.12)', color: '#3b82f6', label: 'Mail verstuurd' },
  resolved: { bg: 'rgba(16,185,129,0.12)', color: '#10b981', label: 'Opgelost' },
  expired: { bg: 'rgba(148,163,184,0.12)', color: '#94a3b8', label: 'Verlopen' },
};

export default function PaymentRecoveriesPage() {
  const { getToken } = useAuth();
  const token = getToken();
  const [data, setData] = useState({ recoveries: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [resending, setResending] = useState(null);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await axios.get(`${BACKEND}/api/admin/payment-recoveries`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon recoveries niet laden');
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token, filter]);

  const resend = async (id) => {
    setResending(id);
    try {
      await axios.post(`${BACKEND}/api/admin/payment-recoveries/${id}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Retry-mail opnieuw verstuurd');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verzenden mislukt');
    } finally {
      setResending(null);
    }
  };

  const copyRetryUrl = (token) => {
    const url = `${BACKEND}/api/public/payment-retry/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Retry-link gekopieerd');
  };

  const stats = data.stats || {};
  const filters = ['', 'pending', 'sent', 'resolved', 'expired'];

  return (
    <div style={{ padding: 24, color: 'white', maxWidth: 1200 }} data-testid="payment-recoveries-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 28, fontWeight: 800, margin: 0 }}>Failed Payment Recovery</h1>
          <p style={{ color: '#94a3b8', marginTop: 4, fontSize: 14 }}>Automatische 1-klik retry-flow voor mislukte Stripe betalingen. Klanten krijgen mail met retry-link (7 dagen geldig).</p>
        </div>
        <button
          data-testid="refresh-btn"
          onClick={load}
          style={{ background: 'transparent', border: '1px solid #334155', color: 'white', padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={14} /> Ververs
        </button>
      </div>

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Totaal" value={stats.total ?? 0} color="#94a3b8" />
        <StatCard label="Open" value={stats.open ?? 0} color="#f59e0b" icon={AlertTriangle} />
        <StatCard label="Opgelost" value={stats.resolved ?? 0} color="#10b981" icon={CheckCircle2} />
        <StatCard label="Max retries bereikt" value={stats.at_max_retries ?? 0} color="#ef4444" icon={AlertTriangle} testId="stat-max-retries" />
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {filters.map((f) => (
          <button
            key={f || 'all'}
            data-testid={`filter-${f || 'all'}`}
            onClick={() => setFilter(f)}
            style={{
              padding: '6px 14px', borderRadius: 8, border: '1px solid #334155',
              background: filter === f ? '#D4A017' : 'transparent',
              color: filter === f ? '#0f172a' : 'white',
              fontWeight: 700, fontSize: 12, cursor: 'pointer', textTransform: 'capitalize',
            }}
          >
            {f ? (STATUS_STYLES[f]?.label || f) : 'Alles'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
          <Loader2 size={24} className="animate-spin" style={{ display: 'inline' }} /> Laden…
        </div>
      ) : data.recoveries.length === 0 ? (
        <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 12, padding: 40, textAlign: 'center' }} data-testid="empty-state">
          <Mail size={32} color="#334155" style={{ margin: '0 auto 12px' }} />
          <p style={{ color: '#94a3b8' }}>Geen recovery-pogingen gevonden. Alle betalingen zijn tot nu toe succesvol.</p>
        </div>
      ) : (
        <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }} data-testid="recoveries-table">
              <thead>
                <tr style={{ color: '#94a3b8', textAlign: 'left', background: '#1e293b' }}>
                  <th style={thStyle}>Datum</th>
                  <th style={thStyle}>Klant</th>
                  <th style={thStyle}>Bedrag</th>
                  <th style={thStyle}>Reden</th>
                  <th style={thStyle}>Poging</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Acties</th>
                </tr>
              </thead>
              <tbody>
                {data.recoveries.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid #1e293b' }} data-testid={`recovery-row-${r.id}`}>
                    <td style={tdStyle}>
                      <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#94a3b8' }}>
                        {(r.created_at || '').slice(0, 16).replace('T', ' ')}
                      </span>
                    </td>
                    <td style={tdStyle}>{r.user_email}</td>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>€{Number(r.amount).toFixed(2)}</td>
                    <td style={{ ...tdStyle, maxWidth: 260, fontSize: 12, color: '#cbd5e1' }}>{r.reason}</td>
                    <td style={tdStyle}>
                      <span style={{ color: r.attempt_number >= 3 ? '#ef4444' : '#94a3b8', fontWeight: 700 }}>
                        {r.attempt_number}/3
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        background: STATUS_STYLES[r.status]?.bg || '#1e293b',
                        color: STATUS_STYLES[r.status]?.color || 'white',
                        padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                      }}>
                        {STATUS_STYLES[r.status]?.label || r.status}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          data-testid={`copy-link-${r.id}`}
                          onClick={() => copyRetryUrl(r.retry_token)}
                          disabled={r.status === 'resolved'}
                          title="Kopieer retry-link"
                          style={iconBtn(r.status === 'resolved')}
                        >
                          <Copy size={13} />
                        </button>
                        <button
                          data-testid={`resend-${r.id}`}
                          onClick={() => resend(r.id)}
                          disabled={r.status === 'resolved' || resending === r.id}
                          title="Mail opnieuw versturen"
                          style={iconBtn(r.status === 'resolved' || resending === r.id)}
                        >
                          {resending === r.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon, testId }) {
  return (
    <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 12, padding: 16 }} data-testid={testId}>
      <p style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
        {Icon && <Icon size={12} color={color} />} {label}
      </p>
      <p style={{ fontSize: 28, fontWeight: 800, color, margin: '6px 0 0' }}>{value}</p>
    </div>
  );
}

const thStyle = { padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 };
const tdStyle = { padding: '10px 12px', color: 'white' };
const iconBtn = (disabled) => ({
  background: disabled ? '#1e293b' : '#334155',
  border: 'none', color: disabled ? '#475569' : 'white',
  padding: 6, borderRadius: 6, cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
});
